import React, { useState, useEffect } from "react";
import { Menu, X, Feather } from "lucide-react";
import { useLocation, Link } from "react-router-dom";

const menuItems = [
  { label: "Services", id: "services" },
  { label: "Portfolio", id: "preview" },
  { label: "Process", id: "process" },
  { label: "Testimonials", id: "testimonials" },
  { label: "Pricing", id: "pricing" },
  { label: "Journal", id: "articles" },
];

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const headerOffset = 100;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Track active section
      const sections = menuItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 150;

      sections.forEach((section, index) => {
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;
          if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
            setActiveSection(menuItems[index].id);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const isHomepage = location.pathname === "/";

  const handleNavClick = (sectionId) => {
    if (isHomepage) {
      scrollToSection(sectionId);
    } else {
      window.location.href = `/#${sectionId}`;
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-500 ease-luxury ${
          isScrolled
            ? "bg-cream-50/95 backdrop-blur-md shadow-luxury py-3"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              onClick={() => isHomepage && scrollToSection("hero")}
              className="flex items-center gap-3 group"
            >
              <div className={`p-2 rounded-lg transition-all duration-300 ${
                isScrolled ? "bg-ink-900" : "bg-ink-900/90"
              }`}>
                <Feather className="w-6 h-6 text-gold-400" />
              </div>
              <div className="flex flex-col">
                <span className={`font-display text-2xl font-bold tracking-tight transition-colors duration-300 ${
                  isScrolled ? "text-ink-900" : "text-ink-900"
                }`}>
                  WriteToSite
                </span>
                <span className={`text-xs font-sans tracking-[0.2em] uppercase transition-colors duration-300 ${
                  isScrolled ? "text-ink-500" : "text-ink-600"
                }`}>
                  Author Websites
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`relative px-4 py-2 font-sans text-sm tracking-wide transition-all duration-300 group ${
                    activeSection === item.id
                      ? "text-gold-600"
                      : isScrolled
                      ? "text-ink-700 hover:text-ink-900"
                      : "text-ink-700 hover:text-ink-900"
                  }`}
                >
                  {item.label}
                  <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gold-500 transition-all duration-300 ${
                    activeSection === item.id ? "w-6" : "w-0 group-hover:w-6"
                  }`} />
                </button>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              {isHomepage && (
                <button
                  onClick={() => scrollToSection("contact")}
                  className="btn-primary"
                >
                  <span className="flex items-center gap-2">
                    Begin Your Journey
                    <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`lg:hidden p-2 transition-colors duration-300 ${
                isScrolled ? "text-ink-900" : "text-ink-900"
              }`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isMenuOpen ? "visible" : "invisible"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-ink-900/50 backdrop-blur-sm transition-opacity duration-500 ${
            isMenuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-cream-50 shadow-luxury-lg transition-transform duration-500 ease-luxury ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-6 border-b border-cream-200">
              <span className="font-display text-xl font-semibold text-ink-900">Menu</span>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 text-ink-700 hover:text-ink-900 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 p-6">
              <ul className="space-y-1">
                {menuItems.map((item, index) => (
                  <li
                    key={item.id}
                    style={{ animationDelay: `${index * 0.05}s` }}
                    className={`transform transition-all duration-500 ${
                      isMenuOpen ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0"
                    }`}
                  >
                    <button
                      onClick={() => handleNavClick(item.id)}
                      className={`w-full flex items-center gap-4 px-4 py-3 font-sans text-lg transition-all duration-300 rounded-lg ${
                        activeSection === item.id
                          ? "bg-gold-100 text-gold-700"
                          : "text-ink-700 hover:bg-cream-100 hover:text-ink-900"
                      }`}
                    >
                      <span className="w-6 h-px bg-gold-400" />
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Menu Footer */}
            {isHomepage && (
              <div className="p-6 border-t border-cream-200">
                <button
                  onClick={() => {
                    scrollToSection("contact");
                    setIsMenuOpen(false);
                  }}
                  className="w-full btn-gold"
                >
                  <span>Begin Your Journey</span>
                </button>
              </div>
            )}

            {/* Decorative Element */}
            <div className="p-6 pt-0">
              <div className="flex items-center gap-3 text-ink-400">
                <Feather className="w-4 h-4 text-gold-500" />
                <span className="text-xs font-sans tracking-widest uppercase">
                  Crafting Stories Online
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
